import * as React from "react"
import { useEffect, useMemo, useState } from "react"

import { cn } from "@/lib/utils"

import {
  getLoadingChartTileStyle,
  type LoadingChartTileKind,
} from "./loading-chart-tile-styles"
import "./loading-chart.css"

type Tile = {
  row: number
  col: number
  kind: LoadingChartTileKind
}

/**
 * Static candle-tile grid used on marketing heroes (e.g. Strategies chartBackdrop).
 * Frozen — no reveal wave, no loop, no centre logo.
 */
export function FrozenLoadingChartBackdrop({
  className,
}: {
  className?: string
}): React.ReactElement {
  const [viewport, setViewport] = useState({ width: 1200, height: 800 })

  useEffect(() => {
    const read = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight })
    }
    read()
    window.addEventListener("resize", read)
    return () => window.removeEventListener("resize", read)
  }, [])

  const { tiles, cols, tileSize, gap } = useMemo(() => {
    const tileSize = viewport.width < 768 ? 28 : 62
    const gap = viewport.width < 768 ? 4 : 8
    const cols = Math.ceil((viewport.width + gap) / (tileSize + gap))
    const rows = Math.ceil((viewport.height + gap) / (tileSize + gap))

    // Match marketing hero default: uptrendA, no logo cutout.
    const upStart = rows - Math.max(3, Math.floor(rows * 0.22))
    const upEnd = Math.max(1, Math.floor(rows * 0.2))
    const chartRows: number[] = []
    let currentRow = upStart
    for (let col = 0; col < cols; col += 1) {
      const progress = cols <= 1 ? 1 : col / (cols - 1)
      const target = Math.round(upStart + (upEnd - upStart) * progress)
      const drift = target < currentRow ? -1 : target > currentRow ? 1 : 0
      const jitterSeed = (col * 17 + rows * 13) % 7
      const jitter = jitterSeed === 0 ? -1 : jitterSeed === 6 ? 1 : 0
      const step = Math.max(-1, Math.min(1, drift + jitter))
      currentRow = Math.max(1, Math.min(rows - 2, currentRow + step))
      chartRows.push(currentRow)
    }

    const chartByCell = new Map<string, LoadingChartTileKind>()
    for (let col = 0; col < cols; col += 1) {
      const curr = chartRows[col] ?? upStart
      const prev = col === 0 ? curr : (chartRows[col - 1] ?? curr)
      const color: LoadingChartTileKind = curr <= prev ? "green" : "red"
      const minRow = Math.min(prev, curr)
      const maxRow = Math.max(prev, curr)
      for (let row = minRow; row <= maxRow; row += 1) {
        chartByCell.set(`${row}-${col}`, color)
      }
      chartByCell.set(`${curr}-${col}`, color)
    }

    const allTiles: Tile[] = []
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        allTiles.push({
          row,
          col,
          kind: chartByCell.get(`${row}-${col}`) ?? "grey",
        })
      }
    }

    return { tiles: allTiles, cols, tileSize, gap }
  }, [viewport.height, viewport.width])

  const variant = viewport.width < 768 ? "mobile" : "desktop"

  return (
    <div
      className={cn(
        "tessera-loading-chart-overlay tessera-loading-chart-overlay--embedded tessera-loading-chart-overlay--frozen",
        className
      )}
      aria-hidden
    >
      <div className="tessera-loading-chart-stage">
        <div
          className="tessera-loading-chart-grid"
          style={{
            gap: `${gap}px`,
            gridTemplateColumns: `repeat(${cols}, ${tileSize}px)`,
          }}
        >
          {tiles.map((tile) => (
            <div
              key={`${tile.row}-${tile.col}`}
              className="tessera-loading-chart-tile tessera-loading-chart-tile--visible"
              style={{
                width: `${tileSize}px`,
                height: `${tileSize}px`,
                gridRow: tile.row + 1,
                gridColumn: tile.col + 1,
                ...getLoadingChartTileStyle(tile.kind, variant),
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
